import { Injectable } from '@angular/core';

export interface PaginationConfig {
    currentPage: number;
    pageSize: number;
    totalItems: number;
}

export interface PaginationResult<T> {
    items: T[];
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startIndex: number;
    endIndex: number;
}

@Injectable({
    providedIn: 'root'
})
export class TablePaginationService {

    /**
     * Paginate an array of items
     * @param items - Array of items to paginate
     * @param currentPage - Current page number (1-based)
     * @param pageSize - Number of items per page (default: 20)
     * @returns PaginationResult with paginated data and metadata
     */
    paginate<T>(items: T[], currentPage: number, pageSize: number = 20): PaginationResult<T> {
        const totalItems = items.length;
        const totalPages = Math.ceil(totalItems / pageSize);

        // Ensure currentPage is within valid bounds
        const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

        // Calculate start and end indices
        const startIndex = (validCurrentPage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, totalItems);

        // Get paginated items
        const paginatedItems = items.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            currentPage: validCurrentPage,
            pageSize,
            totalItems,
            totalPages,
            hasNextPage: validCurrentPage < totalPages,
            hasPreviousPage: validCurrentPage > 1,
            startIndex: startIndex + 1, // 1-based for display
            endIndex: endIndex
        };
    }

    /**
     * Generate page numbers for pagination controls
     * @param currentPage - Current page number
     * @param totalPages - Total number of pages
     * @param maxPagesToShow - Maximum number of page buttons to show
     * @returns Array of page numbers to display
     */
    generatePageNumbers(currentPage: number, totalPages: number, maxPagesToShow: number = 5): number[] {
        if (totalPages <= maxPagesToShow) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const halfPages = Math.floor(maxPagesToShow / 2);
        let startPage = Math.max(1, currentPage - halfPages);
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        // Adjust if we're near the end
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }

    /**
     * Get pagination info text (e.g., "Showing 1 to 10 of 50 courses")
     * @param result - Pagination result
     * @param itemDescriptor - Descriptor for the items (e.g., 'courses', 'players', 'tournaments')
     * @returns Formatted info string
     */
    getPaginationInfo<T>(result: PaginationResult<T>, itemDescriptor: string = 'Entries'): string {
        if (result.totalItems === 0) {
            return `Showing 0 to 0 of 0 ${itemDescriptor}`;
        }

        return `Showing ${result.startIndex} to ${result.endIndex} of ${result.totalItems} ${itemDescriptor}`;
    }
}
